/**
 * Screen-share capture subsystem for voice — extracted from voiceMedia's
 * controller so the god file stays focused on snapshot orchestration.
 * This owns the screen capture (Electron picker vs browser getDisplayMedia),
 * the native screen-audio strategies (per-app / system-without-Bunker /
 * legacy mix), the live quality controls (manual + auto), and the SFU screen
 * producers. It reaches back into the controller only
 * through the narrow {@link ScreenShareHost} interface.
 */
import i18n from '@/i18n';
import { getSocket } from '@/lib/socket';
import { toast } from '@/lib/toast';
import {
  getVoicePreferences,
  buildScreenShareConstraints,
  updateVoicePreferences,
  screenMaxBitrate,
  type ScreenFps,
  type ScreenResolution,
} from '@/lib/voicePreferences';
import {
  getLastScreenAudioFailure,
  startScreenAudioTrack,
  startSystemScreenAudioTrack,
  type ScreenAudioCapture,
} from '@/lib/voice/screenAudioCapture';
import {
  planScreenAudioStrategies,
  type ScreenAudioStrategy,
} from '@/lib/voice/screenShareAudioPlan';
import {
  buildQualityLadder,
  nextAutoQuality,
  AUTO_QUALITY_INITIAL,
  type AutoQualityState,
  type ScreenQualityStep,
} from '@/lib/voice/screenShareAutoQuality';
import { normalizeMediaError } from '@/lib/voice/voiceMediaHelpers';

/** Pixel heights per named resolution tier — duplicated from voicePreferences
 *  to keep the constraint math local; the renderer-side picker doesn't import
 *  voicePreferences internals. */
const SCREEN_HEIGHTS = {
  '720p': 720,
  '1080p': 1080,
  '1440p': 1440,
  '4k': 2160,
} as const;

/** An acquired screen-audio source: the ScreenAudioCapture shape plus which
 *  strategy produced it (drives the degradation toasts + silence watchdog). */
interface AcquiredScreenAudio {
  track: MediaStreamTrack;
  stop: () => void;
  strategy: ScreenAudioStrategy;
  stats: (() => { chunks: number; nonSilentChunks: number }) | null;
}

/**
 * Legacy raw system loopback via Chromium's desktop capture — the full mix
 * INCLUDING the call output (listeners may hear themselves). Kept as the last
 * resort for browsers and old shells without the native exclude capture.
 */
async function captureLegacySystemMix(): Promise<AcquiredScreenAudio | null> {
  try {
    const sysStream = await navigator.mediaDevices.getUserMedia({
      video: {
        mandatory: { chromeMediaSource: 'desktop', chromeMediaSourceId: 'screen:0:0' },
      } as unknown as MediaTrackConstraints,
      audio: {
        mandatory: { chromeMediaSource: 'desktop' },
        suppressLocalAudioPlayback: true,
        optional: [
          { echoCancellation: false },
          { noiseSuppression: false },
          { autoGainControl: false },
        ],
      } as unknown as MediaTrackConstraints,
    });
    const sysAudio = sysStream.getAudioTracks()[0];
    // The rider video track is unavoidable with chromeMediaSource — stop it
    // immediately, we only want the loopback audio.
    sysStream.getVideoTracks().forEach((t) => t.stop());
    if (!sysAudio) return null;
    return {
      track: sysAudio,
      stop: () => sysAudio.stop(),
      strategy: 'system-mix',
      stats: null,
    };
  } catch {
    return null;
  }
}

/** Try one strategy; null → caller moves on to the next. */
async function tryStrategy(
  strategy: ScreenAudioStrategy,
  sourceId: string,
): Promise<AcquiredScreenAudio | null> {
  if (strategy === 'app' || strategy === 'system-clean') {
    const cap: ScreenAudioCapture | null =
      strategy === 'app'
        ? await startScreenAudioTrack(sourceId)
        : await startSystemScreenAudioTrack();
    if (!cap) return null;
    const actual = cap.mode === 'system-mix' ? 'system-mix' : strategy;
    return { track: cap.track, stop: cap.stop, strategy: actual, stats: cap.stats };
  }
  return captureLegacySystemMix();
}

/** Walk the strategy plan; returns the first capture that starts. */
async function acquireScreenAudio(
  plan: ScreenAudioStrategy[],
  sourceId: string,
): Promise<AcquiredScreenAudio | null> {
  for (const strategy of plan) {
    const acquired = await tryStrategy(strategy, sourceId);
    if (acquired) return acquired;
  }
  return null;
}

/** Surface a degradation to the user: they asked for the app's own sound (or
 *  clean system sound) but got a broader/echo-prone capture instead. */
function notifyAudioDegradation(requested: ScreenAudioStrategy, got: ScreenAudioStrategy) {
  if (got === requested) return;
  if (got === 'system-clean') {
    toast.warning(i18n.t('voice.screenAudioCleanFallback'), { duration: 6000 });
  } else if (got === 'system-mix') {
    toast.warning(i18n.t('voice.screenAudioMixFallback'), { duration: 8000 });
  }
}

/**
 * Capture the user's screen-share video stream (and kick off audio capture
 * when requested).
 *
 * - In the Electron shell (when `bunkerDesktop.pickScreenShare` is exposed):
 *   open the Discord-style picker → save the user's quality choice into the
 *   voice preferences → call `getUserMedia` with `chromeMediaSource: 'desktop'`
 *   so the chosen source id is honoured directly (bypasses the system picker).
 *   Audio comes from the native WASAPI strategies (see screenShareAudioPlan) —
 *   never from the video getUserMedia, so whole-screen shares no longer
 *   re-capture the call output.
 * - In a normal browser: fall back to `getDisplayMedia` with constraints built
 *   from the saved preferences (the browser shows its own picker).
 */
async function captureScreenStream(): Promise<{
  stream: MediaStream;
  audio: AcquiredScreenAudio | null;
  /** Did the sharer ask for sound? Distinguishes "shared silently on purpose"
   *  from "asked for sound and didn't get it" in the sharer's own indicator. */
  audioWanted: boolean;
  /** Encoder target for the screen producer, scaled to the chosen
   *  resolution + fps so high tiers (1440p/60) aren't starved at the old
   *  flat 4 Mbps. */
  videoMaxBitrate: number;
  quality: ScreenQualityStep;
}> {
  const bridge = window.bunkerDesktop;
  if (bridge?.isDesktop && typeof bridge.pickScreenShare === 'function') {
    const prefs = getVoicePreferences();
    const choice = await bridge.pickScreenShare({
      fps: prefs.screenFps,
      resolution: prefs.screenResolution,
      // ALWAYS open with sound on, and deliberately do NOT remember the
      // opposite. This used to be persisted (`screenAudioWanted`), which turned
      // one silent share into a permanent one: share once while an older client
      // was still loaded (picker seeded OFF) and `false` was written to
      // localStorage, so every later share opened muted with no hint why. A
      // shared game/video is expected to be audible; sharing silently is a
      // per-share exception, so it stays a per-share toggle.
      audio: true,
    });
    if (!choice) throw new DOMException('User cancelled', 'NotAllowedError');
    // Persist the picker's quality choice so the next session starts there.
    // Sound is intentionally absent — see the `audio: true` note above.
    updateVoicePreferences({
      screenFps: choice.fps,
      screenResolution: choice.resolution,
    });
    const videoMandatory: Record<string, string | number> = {
      chromeMediaSource: 'desktop',
      chromeMediaSourceId: choice.sourceId,
      maxFrameRate: choice.fps,
    };
    if (choice.resolution !== 'source') {
      const h = SCREEN_HEIGHTS[choice.resolution];
      videoMandatory.maxHeight = h;
      videoMandatory.maxWidth = Math.round(h * (16 / 9));
    }
    // `chromeMediaSource` lives outside the standard MediaTrackConstraints
    // type — cast through unknown to satisfy TS while Chromium reads it.
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { mandatory: videoMandatory } as unknown as MediaTrackConstraints,
      audio: false,
    });

    let audio: AcquiredScreenAudio | null = null;
    if (choice.audio) {
      const isScreen = choice.sourceId.startsWith('screen:');
      const plan = planScreenAudioStrategies({
        isScreen,
        forceSystem: Boolean(choice.audioFallback) || prefs.screenAudioFallback,
        hasNativeApp: typeof bridge.startScreenAudio === 'function',
        hasNativeSystem: typeof bridge.startSystemScreenAudio === 'function',
      });
      audio = await acquireScreenAudio(plan, choice.sourceId);
      const requested = plan[0];
      if (audio && requested) notifyAudioDegradation(requested, audio.strategy);
      if (audio) {
        stream.addTrack(audio.track);
      } else {
        // Every strategy failed. Sharing continues (picture is the point), but
        // say so — this used to be a silent no-op, so the sharer had no idea
        // viewers were getting a mute stream. The reason rides along: the last
        // one («blob: blocked by CSP») was invisible for weeks.
        toast.warning(i18n.t('voice.screenAudioUnavailable'), { duration: 8000 });
      }
    }
    return {
      stream,
      audio,
      audioWanted: choice.audio,
      videoMaxBitrate: screenMaxBitrate(choice.resolution, choice.fps),
      quality: { resolution: choice.resolution, fps: choice.fps },
    };
  }
  // Browser path — the OS / browser picker shows up as part of getDisplayMedia.
  const prefs = getVoicePreferences();
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: buildScreenShareConstraints(),
    audio: { suppressLocalAudioPlayback: true } as MediaTrackConstraints,
  });
  const browserAudio = stream.getAudioTracks()[0] ?? null;
  if (!browserAudio) {
    // A browser share with no audio track is NORMAL and entirely the browser's
    // rules, not a bug we can fix — but it used to be completely silent about
    // it, which is how «включил демку, друг не слышит» survived three rounds:
    //   - a window / tab share: Chromium offers no system audio for it at all;
    //   - a whole-screen share: audio only arrives if the user ticks «Также
    //     предоставить доступ к аудиосистеме», which is OFF by default.
    // So say which case it is and what to do instead.
    // getSettings is optional on purpose: older engines (and test doubles) may
    // not implement it, and a missing surface only costs us message precision.
    const videoTrack = stream.getVideoTracks()[0] as
      | (MediaStreamTrack & { getSettings?: () => { displaySurface?: string } })
      | undefined;
    const surface = videoTrack?.getSettings?.().displaySurface;
    toast.warning(
      i18n.t(surface === 'monitor' ? 'voice.screenAudioTickBox' : 'voice.screenAudioWindowUnsupported'),
      { duration: 12000 },
    );
  }
  return {
    stream,
    // The browser picker always offers "share tab audio", so sound is always
    // requested here; whether the user ticked it shows up as a missing track.
    audioWanted: true,
    audio: browserAudio
      ? {
          track: browserAudio,
          stop: () => browserAudio.stop(),
          strategy: 'system-mix',
          stats: null,
        }
      : null,
    videoMaxBitrate: screenMaxBitrate(prefs.screenResolution, prefs.screenFps),
    quality: { resolution: prefs.screenResolution, fps: prefs.screenFps },
  };
}

/** What the screen-share session needs from the voice controller. Implemented
 *  with closures so the controller's internals stay private. */
export interface ScreenShareHost {
  getActiveChannelId(): string | null;
  /** /api/rtc/config has answered — voice media may connect. */
  isMediaReady(): boolean;
  getLocalScreenStream(): MediaStream | null;
  /** Adopt a fresh capture into the snapshot (and clear any prior error). */
  onScreenStreamStarted(stream: MediaStream): void;
  /** Drop the capture from the snapshot. Called UNCONDITIONALLY by
   *  stopLocalStream(); the implementation must guard against "no stream set"
   *  so a redundant stop doesn't fire a spurious state emission. */
  onScreenStreamStopped(): void;
  recordDebug(event: string): void;
  produceScreen(channelId: string, track: MediaStreamTrack, maxBitrate: number): Promise<void>;
  produceScreenAudio(channelId: string, track: MediaStreamTrack): Promise<void>;
  closeScreenProducer(channelId: string | null): Promise<void>;
  closeScreenAudioProducer(channelId: string | null): Promise<void>;
  /** Swap the live screen-audio producer's track (silence watchdog fallback).
   *  No-op when there is no active screen-audio producer. */
  replaceScreenAudioTrack(track: MediaStreamTrack): Promise<void>;
  /** Retarget the screen producer's encoder bitrate (live quality change). */
  updateScreenMaxBitrate(maxBitrate: number): Promise<void>;
  /** Stats of the screen video RTCRtpSender (auto quality sampling), or null
   *  when not producing. */
  getScreenSenderStats(): Promise<RTCStatsReport | null>;
}

/** How long the per-app capture may stay fully silent before the watchdog
 *  assumes the wrong process is hooked and falls back to system audio. */
const SILENCE_WATCHDOG_MS = 8000;
/** Auto-quality stats sampling period. */
const AUTO_QUALITY_TICK_MS = 3000;

export interface ScreenShareQualityState {
  /** The tier the user picked (ladder level 0). */
  resolution: ScreenResolution;
  fps: ScreenFps;
  /** Adaptive mode on/off (mirrors the preference while sharing). */
  auto: boolean;
  /** What is actually applied right now (differs from the picked tier when
   *  auto mode stepped down). Null when not sharing. */
  applied: ScreenQualityStep | null;
  /**
   * State of the SOUND half of the share, so the sharer can see at a glance
   * whether viewers get audio. Every «друг не слышит» report so far was
   * indistinguishable from working, because nothing on screen said otherwise.
   *
   *  - `off`      — sharing without sound (picker toggle off).
   *  - `silent`   — a track is being sent but it has carried no signal yet
   *                 (wrong process hooked, or the source is genuinely quiet).
   *  - `sending`  — sound captured and published.
   *  - `failed`   — audio was requested but could not be captured/published.
   */
  audio: 'off' | 'silent' | 'sending' | 'failed';
}

export class VoiceScreenShareSession {
  // Stops the native screen-audio capture (desktop only) when sharing ends.
  private screenAudioStop: (() => void) | null = null;
  private audioStrategy: ScreenAudioStrategy | null = null;
  private audioStats: (() => { chunks: number; nonSilentChunks: number }) | null = null;
  /** Sound half of the share, surfaced through getQualityState().audio. */
  private audioState: 'off' | 'silent' | 'sending' | 'failed' = 'off';
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Monotonic share-session counter. Bumped on every teardown/adoption so a
   *  delayed async callback (the silence watchdog awaits a native capture
   *  start) can detect that "a share is active" is not "MY share is active"
   *  and must not touch the newer session's producer/stop handles. */
  private sessionGen = 0;

  // Live quality state (valid while sharing).
  private videoTrack: MediaStreamTrack | null = null;
  private quality: ScreenQualityStep | null = null;
  private ladder: ScreenQualityStep[] = [];
  private autoState: AutoQualityState = AUTO_QUALITY_INITIAL;
  private autoTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private host: ScreenShareHost) {}

  async setEnabled(enabled: boolean): Promise<boolean> {
    if (!enabled) {
      await this.disable();
      return true;
    }
    // Validate media + channel readiness BEFORE capturing. Otherwise a
    // failure *after* getDisplayMedia would leave the browser actively
    // capturing the screen while Bunker thinks screenOn=false (Vasily P0).
    if (!this.host.isMediaReady()) throw new Error('media_not_ready');
    const channelId = this.host.getActiveChannelId();
    if (!channelId) throw new Error('sfu_not_in_channel');

    const { stream, audio, audioWanted, videoMaxBitrate, quality } = await captureScreenStream().catch(
      (err) => {
        throw normalizeMediaError(err, 'screen');
      },
    );
    const track = stream.getVideoTracks()[0] ?? null;
    if (!track) {
      audio?.stop();
      for (const ownedTrack of stream.getTracks()) {
        ownedTrack.stop();
      }
      throw new Error('screen_access_failed');
    }
    // Tell the encoder this is detailed content (text/UI), so it preserves
    // resolution/sharpness over frame rate under bandwidth pressure — the
    // difference between a crisp shared editor and a blurry mush.
    try {
      track.contentHint = 'detail';
    } catch {
      /* contentHint unsupported — harmless */
    }
    this.stopLocalStream();
    this.host.onScreenStreamStarted(stream);
    // Adopt the new capture's audio-stop AFTER stopLocalStream tore down
    // any previous one (it stops `screenAudioStop`), so we don't kill the fresh one.
    this.screenAudioStop = audio?.stop ?? null;
    this.audioStrategy = audio?.strategy ?? null;
    this.audioStats = audio?.stats ?? null;
    this.videoTrack = track;
    this.quality = quality;
    this.ladder = buildQualityLadder(quality);
    this.autoState = AUTO_QUALITY_INITIAL;
    track.onended = () => {
      void this.disable();
      getSocket().emit('voice:state', { screenOn: false });
    };
    try {
      await this.host.produceScreen(channelId, track, videoMaxBitrate);
    } catch (err) {
      // Production failed after capture — tear the capture down so we never
      // leave the browser screen-grabbing with no producer behind it.
      this.stopLocalStream();
      throw err;
    }
    // Screen audio rides as a separate producer so listeners hear it. A failure
    // here must NOT kill the video share — but it must not be invisible either:
    // this was a debug-log-only catch, so "captured fine, never published" and
    // "viewers hear nothing" looked identical from the sharer's side.
    const audioTrack = stream.getAudioTracks()[0] ?? null;
    if (audioTrack) {
      let produced = true;
      await this.host.produceScreenAudio(channelId, audioTrack).catch((err) => {
        produced = false;
        this.host.recordDebug(
          `sfu:screen-audio:error:${err instanceof Error ? err.message : 'unknown'}`,
        );
        toast.warning(i18n.t('voice.screenAudioNotSent'), { duration: 8000 });
      });
      this.audioState = produced ? 'silent' : 'failed';
      if (produced) this.startSilenceWatchdog();
    } else {
      // No audio track at all: either the sharer turned sound off in the picker,
      // or every capture strategy failed (which already toasted above). Keep the
      // reason in the debug trail — the last one («blob: worklet blocked by
      // CSP») was invisible for weeks precisely because nothing recorded it.
      this.audioState = audioWanted ? 'failed' : 'off';
      if (audioWanted) {
        this.host.recordDebug(`screen-audio:failed:${getLastScreenAudioFailure() ?? 'unknown'}`);
      }
    }
    if (getVoicePreferences().screenQualityAuto) this.startAutoQuality();
    return true;
  }

  async disable(): Promise<void> {
    const channelId = this.host.getActiveChannelId();
    await this.host.closeScreenProducer(channelId);
    await this.host.closeScreenAudioProducer(channelId);
    this.stopLocalStream();
  }

  stopLocalStream(): void {
    this.sessionGen++;
    this.stopSilenceWatchdog();
    this.stopAutoQuality();
    // Kill the native screen-audio capture (addon + audio graph) first.
    this.screenAudioStop?.();
    this.screenAudioStop = null;
    this.audioStrategy = null;
    this.audioStats = null;
    this.audioState = 'off';
    this.videoTrack = null;
    this.quality = null;
    this.ladder = [];
    for (const track of this.host.getLocalScreenStream()?.getTracks() ?? []) {
      track.stop();
    }
    this.host.onScreenStreamStopped();
  }

  // ── Silence watchdog ───────────────────────────────────────────────────────
  // A per-app capture can "start" fine yet never deliver sound (launcher PID
  // hooked instead of the game, anti-cheat blocking the loopback, the app
  // rendering on a device our hook misses). After a grace period with zero
  // non-silent chunks we swap the producer onto the system capture (all PC
  // audio minus Bunker) so listeners actually hear something, and tell the
  // sharer what happened.

  private startSilenceWatchdog(): void {
    this.stopSilenceWatchdog();
    if (this.audioStrategy !== 'app' || !this.audioStats) return;
    const gen = this.sessionGen;
    this.silenceTimer = setTimeout(() => {
      void this.checkSilenceAndFallback(gen);
    }, SILENCE_WATCHDOG_MS);
  }

  private stopSilenceWatchdog(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  private async checkSilenceAndFallback(gen: number): Promise<void> {
    this.silenceTimer = null;
    if (gen !== this.sessionGen) return; // a newer share owns the state now
    const stats = this.audioStats?.();
    // Chunks flowed AND at least one carried signal — the app is audible (or
    // genuinely silent right now, in which case chunks still tick with zeros
    // only when the session renders; either way, keep the clean per-app hook).
    if (!stats || stats.nonSilentChunks > 0 || this.audioStrategy !== 'app') return;
    const fallback = await startSystemScreenAudioTrack();
    if (!fallback) {
      // Old shell — nothing better available; nudge the user to the manual
      // «Альтернативный звук» toggle instead of leaving them silent+confused.
      if (gen === this.sessionGen) {
        toast.warning(i18n.t('voice.screenAudioSilentHint'), { duration: 8000 });
      }
      return;
    }
    // The share may have been torn down — or replaced by a NEWER share —
    // while we awaited the fallback start. Touching this.* would corrupt the
    // newer session's producer/stop handles.
    if (gen !== this.sessionGen || !this.videoTrack) {
      fallback.stop();
      return;
    }
    try {
      await this.host.replaceScreenAudioTrack(fallback.track);
    } catch {
      // The swap failed — the sharer is still broadcasting the silent per-app
      // capture. Say so instead of letting them believe audio works (the
      // sibling exit paths of this watchdog all notify).
      fallback.stop();
      this.host.recordDebug('sfu:screen-audio:replace-failed');
      toast.warning(i18n.t('voice.screenAudioSilentHint'), { duration: 8000 });
      return;
    }
    if (gen !== this.sessionGen) {
      // Torn down while the replace was in flight; the producer is gone.
      fallback.stop();
      return;
    }
    this.screenAudioStop?.();
    this.screenAudioStop = fallback.stop;
    this.audioStrategy = 'system-clean';
    this.audioStats = fallback.stats;
    this.host.recordDebug('sfu:screen-audio:silence-fallback');
    toast.message(i18n.t('voice.screenAudioSilentSwitch'), { duration: 6000 });
  }

  // ── Live quality controls ──────────────────────────────────────────────────

  getQualityState(): ScreenShareQualityState {
    const prefs = getVoicePreferences();
    const base = this.ladder[0] ?? null;
    return {
      resolution: base?.resolution ?? prefs.screenResolution,
      fps: base?.fps ?? prefs.screenFps,
      auto: prefs.screenQualityAuto,
      applied: this.quality,
      audio: this.currentAudioState(),
    };
  }

  /**
   * Sound state for the sharer's indicator. `audioState` records what the
   * publish attempt achieved; the live capture counters then upgrade
   * 'silent' → 'sending' as soon as a chunk with actual signal arrives.
   *
   * Native captures expose counters; the browser/legacy mix path does not, so
   * a published track there is reported as sending (we have no way to tell).
   */
  private currentAudioState(): ScreenShareQualityState['audio'] {
    if (this.audioState !== 'silent') return this.audioState;
    const stats = this.audioStats?.();
    if (!stats) return 'sending';
    return stats.nonSilentChunks > 0 ? 'sending' : 'silent';
  }

  /** User picked a new tier mid-share: re-anchor the ladder on it, persist the
   *  preference, apply immediately. No producer restart — applyConstraints
   *  renegotiates the capture and the encoder follows. */
  async setQuality(step: ScreenQualityStep): Promise<void> {
    updateVoicePreferences({ screenResolution: step.resolution, screenFps: step.fps });
    if (!this.videoTrack) return;
    this.ladder = buildQualityLadder(step);
    this.autoState = AUTO_QUALITY_INITIAL;
    await this.applyQualityStep(step);
  }

  /** Toggle adaptive mode mid-share (also persisted as the preference). */
  setAutoQuality(enabled: boolean): void {
    updateVoicePreferences({ screenQualityAuto: enabled });
    if (!this.videoTrack) return;
    if (enabled) {
      this.startAutoQuality();
    } else {
      this.stopAutoQuality();
      // Snap back to the user's chosen tier when leaving auto mode.
      const base = this.ladder[0];
      if (base) void this.applyQualityStep(base);
    }
  }

  private async applyQualityStep(step: ScreenQualityStep): Promise<void> {
    const track = this.videoTrack;
    if (!track) return;
    this.quality = step;
    const constraints: MediaTrackConstraints = {
      frameRate: { ideal: step.fps, max: step.fps },
    };
    if (step.resolution !== 'source') {
      const h = SCREEN_HEIGHTS[step.resolution];
      constraints.height = { ideal: h };
      constraints.width = { ideal: Math.round(h * (16 / 9)) };
    }
    try {
      await track.applyConstraints(constraints);
    } catch {
      // Overconstrained on this source — keep sharing at the old capture size;
      // the bitrate retarget below still relieves the uplink.
    }
    await this.host
      .updateScreenMaxBitrate(screenMaxBitrate(step.resolution, step.fps))
      .catch(() => {});
    this.host.recordDebug(`sfu:screen-quality:${step.resolution}@${step.fps}`);
  }

  private startAutoQuality(): void {
    this.stopAutoQuality();
    this.autoState = AUTO_QUALITY_INITIAL;
    this.autoTimer = setInterval(() => {
      void this.autoQualityTick();
    }, AUTO_QUALITY_TICK_MS);
  }

  private stopAutoQuality(): void {
    if (this.autoTimer) {
      clearInterval(this.autoTimer);
      this.autoTimer = null;
    }
  }

  private async autoQualityTick(): Promise<void> {
    if (!this.videoTrack || this.ladder.length === 0) return;
    const report = await this.host.getScreenSenderStats().catch(() => null);
    if (!report) return;
    let bandwidthLimited = false;
    let cpuLimited = false;
    report.forEach((entry) => {
      const stat = entry as { type?: string; kind?: string; qualityLimitationReason?: string };
      if (stat.type === 'outbound-rtp' && stat.kind === 'video') {
        if (stat.qualityLimitationReason === 'bandwidth') bandwidthLimited = true;
        if (stat.qualityLimitationReason === 'cpu') cpuLimited = true;
      }
    });
    const { state, changed } = nextAutoQuality(
      this.autoState,
      { bandwidthLimited, cpuLimited },
      this.ladder.length - 1,
    );
    this.autoState = state;
    if (changed) {
      const step = this.ladder[state.level];
      if (step) await this.applyQualityStep(step);
    }
  }
}
